import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { HittingMonitor as NyyHittingMonitor, LoadingSpinner, Dropdown,
    DatePicker, Button, defaultHittingMonitorFilter, CheckboxDropdown } from 'yankees-react-utils';
import { useTranslation } from 'react-i18next';
import { PlayerReport, useOktaQuery, usePitchVideoFn, useMultiVideoFn, Stats, Team } from '../data';
import usePlayerSelector from '../utils/usePlayerSelector';
import { VideoPlayerModal } from './VideoPlayer';
import StatTooltip from './StatTooltip';

const Wrapper = styled.div`
    text-align: left;
`;
const DatePickerWrapper = styled.div`
    max-width: 120px;
`;
const StyledCheckboxDropdown = styled(CheckboxDropdown)`
    .dropdown-toggle {
        width: 90px;
    }
`;

const InputField = ({ label, children }) => (
    <div className="d-flex align-items-center me-4">
        <span className="me-1">{ label }</span>
        {children}
    </div>
);

const getLevelOptions = (translate, battingSeasons) => {
    const extra = [{ Abbr: translate('common.all'), LevelID: null, SortOrder: 0 }, { Abbr: 'MIN', LevelID: 7, SortOrder: 2 }];

    const levels = battingSeasons.map((bs) => bs.Level);

    return extra.concat([...new Set(levels.map((l) => l.LevelID))].map((levelId) => levels.find((l) => l.LevelID === levelId)))
        .sort((a, b) => a.SortOrder - b.SortOrder)
        .map((level) => ({
            name: level.Abbr,
            value: level.LevelID,
        }));
};

const getOrgOptions = (translate, orgs) => {
    const all = [{ name: translate('common.all'), value: null }];

    return all.concat(orgs.map((o) => ({
        name: o.Abbr,
        value: o.OrganizationID,
    })));
};

const getYearOptions = (translate, battingSeasons) => [...new Set(battingSeasons.map((bs) => bs.YearID))].sort((a, b) => b - a).map((year) => ({
    name: `${translate('common.all')} ${year}`,
    value: year,
}));

let abortController = null;

const HittingMonitor = ({ playerid }) => {
    const { t: translate } = useTranslation();
    const Player = usePlayerSelector();
    const pitchVideoFn = usePitchVideoFn();
    const multiVideoFn = useMultiVideoFn();
    const modalContainer = useRef(null);

    const [levels, setLevels] = useState([]);
    const [org, setOrg] = useState({ name: translate('common.all'), value: null });
    const [year, setYear] = useState(null);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [atbatCount, setAtbatCount] = useState(0);
    const [liveFilter, setLiveFilter] = useState({
        ...defaultHittingMonitorFilter,
        batterHand: Player.Bats === 3 ? 2 : Player.Bats,
    });

    const { data: hitmon, isFetching: isFetchingHitmon } = useOktaQuery(
        ['hitmon', {
            playerid,
            yearid: startDate !== null || endDate !== null ? null : year?.value,
            levelids: levels?.filter((l) => l.checked).map((l) => l.value).join(','),
            orgid: org?.value,
            startdate: startDate,
            enddate: endDate,
        }],
        (t) => {
            if (abortController) {
                abortController.abort();
            }
            const levelIds = levels?.filter((l) => l.checked).map((l) => l.value).filter((x) => !!x);
            abortController = new AbortController();
            return PlayerReport.hitmonPitches(t, {
                playerid,
                yearid: startDate !== null || endDate !== null ? null : year?.value,
                levelids: levelIds.length ? levelIds : undefined,
                orgid: org?.value,
                startdate: startDate,
                enddate: endDate,
            }, { signal: abortController.signal });
        },
        {
            enabled: !!levels.length && (!!year?.value || !!startDate || !!endDate),
            onSuccess: (data) => {
                const lastxpas = data?.pitches?.sort((a, b) => (b?.lastxpa) - (a?.lastxpa))[0]?.lastxpa ?? 100;
                setAtbatCount(lastxpas);
                setLiveFilter({ ...liveFilter, lastPAs: lastxpas });
            },
        },
    );

    const isLoading = isFetchingHitmon || !hitmon;
    if (isFetchingHitmon) { return <LoadingSpinner />; }
    return (
        <Wrapper ref={modalContainer}>
            <div className="d-flex justify-content-between align-items-center">
                <div><h4>{translate('components.hitting-monitor.hitting-monitor')}</h4></div>
                <div style={{ zIndex: 999 }}>
                    <HittingMonitorCriteria
                        playerId={playerid}
                        defaultLevelId={Player.Team.Level.LevelID}
                        levels={levels}
                        setLevels={setLevels}
                        org={org}
                        setOrg={setOrg}
                        year={year}
                        setYear={setYear}
                        startDate={startDate}
                        setStartDate={setStartDate}
                        endDate={endDate}
                        setEndDate={setEndDate}
                    />
                </div>
            </div>
            <NyyHittingMonitor
                className="border rounded mt-3 p-3"
                isLoading={isLoading}
                liveFilter={liveFilter}
                setLiveFilter={setLiveFilter}
                pitches={hitmon?.pitches ?? []}
                player={Player}
                pitchVideoFn={pitchVideoFn}
                multiVideoFn={multiVideoFn}
                videoPlayerModal={VideoPlayerModal}
                modalContainer={modalContainer}
                evLaChartSize="md"
                evLaForceBackground={2}
                sprayChartSizeMultipler={1.2}
                statTooltip={StatTooltip}
                hidePerspectiveToggle
                maxPAs={atbatCount}
                enablePlayAllInStatsTable
            />
        </Wrapper>
    );
};

HittingMonitor.propTypes = {
    playerid: PropTypes.number.isRequired,
};

const HittingMonitorCriteria = ({
    playerId, defaultLevelId, levels, setLevels, org, setOrg, year, setYear, startDate, setStartDate, endDate, setEndDate,
}) => {
    const { t: translate } = useTranslation();
    const [innerLevels, setInnerLevels] = useState(levels);
    const [innerOrg, setInnerOrg] = useState(org);
    const [innerYear, setInnerYear] = useState(year);
    const [innerStartDate, setInnerStartDate] = useState(startDate);
    const [innerEndDate, setInnerEndDate] = useState(endDate);

    const { data: allOrgs, isFetching: isFetchingOrgs } = useOktaQuery(['allOrgs'], (t) => Team.bylevel(t, 8));
    const { data: battingSeasons, isFetching: isFetchingBattingSeasons } = useOktaQuery(
        ['battingSeasons', playerId],
        (t) => Stats.seasonBatting(t, { playerid: playerId }),
    );

    const isFetching = isFetchingOrgs || isFetchingBattingSeasons;

    useEffect(() => {
        if (defaultLevelId && battingSeasons && !levels.filter((x) => x.checked).length) {
            const toSet = getLevelOptions(translate, battingSeasons).map((lo) => ({
                ...lo,
                checked: lo.value === defaultLevelId,
            }));
            if (!toSet.filter((x) => x.checked).length) {
                toSet[toSet.length ? 1 : 0].checked = true;
            }
            setLevels(toSet);
        }
    }, [translate, defaultLevelId, battingSeasons, setLevels, levels]);

    useEffect(() => {
        if (battingSeasons && battingSeasons.length && !levels.filter((x) => x.checked).length) {
            setYear(getYearOptions(translate, battingSeasons)[0]);
        }
    }, [translate, battingSeasons, setYear, levels]);

    const applyFilters = () => {
        setLevels(innerLevels);
        setOrg(innerOrg);
        setYear(innerYear);
        setStartDate(innerStartDate);
        setEndDate(innerEndDate);
    };

    useEffect(() => { setInnerLevels(levels); }, [levels]);
    useEffect(() => { setInnerOrg(org); }, [org]);
    useEffect(() => { setInnerYear(year); }, [year]);
    useEffect(() => { setInnerStartDate(startDate); }, [startDate]);
    useEffect(() => { setInnerEndDate(endDate); }, [endDate]);

    const onLevelDropdownChange = (o, key, e) => {
        if (key === 'All' && e.target.checked) {
            setInnerLevels(innerLevels.map((l, i) => ({
                ...l,
                checked: !i,
            })));
        } else {
            const newLevels = innerLevels.map((lo) => ({
                ...lo,
                checked: lo.name === o.name ? e.target.checked : !!lo.checked,
            }));
            if (newLevels[0].checked && newLevels.filter((l) => !!l.checked).length > 1) {
                newLevels[0].checked = false;
            } else if (!newLevels.filter((l) => !!l.checked).length) {
                newLevels[0].checked = true;
            }

            setInnerLevels(newLevels);
        }
    };

    const handleYearSelect = (newYear) => {
        setInnerStartDate(new Date(newYear.value, 0, 1));
        setInnerEndDate(new Date(newYear.value + 1, 1, 15));
        setInnerYear(newYear);
    };

    const selectedLevels = innerLevels.filter((l) => !!l.checked);
    const levelPlaceholder = selectedLevels.length === 1 ? selectedLevels[0].name : `${selectedLevels.length} ${translate('components.hitting-monitor.levels')}`;

    if (isFetching) {
        return <LoadingSpinner />;
    }
    return (
        <div className="border rounded py-2 px-3 d-flex align-items-center bg-white">
            <InputField label={`${translate('components.hitting-monitor.level')}:`}>
                <StyledCheckboxDropdown
                    value={innerLevels}
                    onChange={onLevelDropdownChange}
                    options={innerLevels}
                    titleOverrideFormat
                    title={levelPlaceholder}
                />
            </InputField>
            <InputField label={`${translate('baseball.abbr-opponent')}:`}>
                <Dropdown value={innerOrg} onSelect={setInnerOrg} options={getOrgOptions(translate, allOrgs)} />
            </InputField>
            <InputField label={`${translate('common.date')}:`}>
                <Dropdown value={innerYear} onSelect={handleYearSelect} options={getYearOptions(translate, battingSeasons)} />
            </InputField>
            <InputField label={`${translate('common.start')}:`}>
                <DatePickerWrapper><DatePicker value={innerStartDate} onSelect={setInnerStartDate} maxDate={innerEndDate} /></DatePickerWrapper>
                <span className="mx-2">{translate('common.to')}</span>
                <DatePickerWrapper>
                    <DatePicker value={innerEndDate} onSelect={setInnerEndDate} minDate={innerStartDate} alignRight />
                </DatePickerWrapper>
            </InputField>
            <Button variant="primary" onClick={applyFilters}>{translate('common.apply')}</Button>
        </div>
    );
};

HittingMonitorCriteria.propTypes = {
    playerId: PropTypes.number.isRequired,
    defaultLevelId: PropTypes.number,
    levels: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string,
        value: PropTypes.number,
    })).isRequired,
    setLevels: PropTypes.func.isRequired,
    org: PropTypes.shape({
        name: PropTypes.string,
        value: PropTypes.number,
    }).isRequired,
    setOrg: PropTypes.func.isRequired,
    year: PropTypes.shape({
        name: PropTypes.string,
        value: PropTypes.number,
    }),
    setYear: PropTypes.func.isRequired,
    startDate: PropTypes.instanceOf(Date),
    setStartDate: PropTypes.func.isRequired,
    endDate: PropTypes.instanceOf(Date),
    setEndDate: PropTypes.func.isRequired,
};

HittingMonitorCriteria.defaultProps = {
    defaultLevelId: null,
    year: null,
    startDate: null,
    endDate: null,
};

export {
    HittingMonitor, HittingMonitorCriteria,
};
